/**
 * Edit Event Dialog
 *
 * Reason: Allows admins to edit existing events in their organization
 */

import { FC, useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Alert,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useQueryClient } from 'react-query';
import { useUpdateEvent, useDeleteEvent, LIST_EVENTS_CACHE_KEY, Event } from '@/adapters/events-v2';

interface EditEventDialogProps {
  open: boolean;
  event: Event | null;
  onClose(): void;
}

/**
 * Dialog for editing existing events
 *
 * @reason Provides form UI for admins to update or delete events
 */
const EditEventDialog: FC<EditEventDialogProps> = ({ open, event, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const updateEventMutation = useUpdateEvent(event?.id || '');
  const deleteEventMutation = useDeleteEvent();

  // Load event data when dialog opens
  useEffect(() => {
    if (event) {
      setName(event.name);
      setDescription(event.description || '');
      setIsActive(event.isActive);
    }
  }, [event]);

  /**
   * Handles form submission to update event
   *
   * @reason Updates event and refreshes the event list
   */
  const handleUpdate = async (): Promise<void> => {
    setError(null);

    // Basic validation
    if (!name.trim()) {
      setError('Event name is required');
      return;
    }

    if (!event) return;

    try {
      await updateEventMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        isActive,
      });

      // Invalidate event list to refresh
      await queryClient.invalidateQueries(LIST_EVENTS_CACHE_KEY);

      // Close dialog and reset form
      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update event';
      setError(errorMessage);
    }
  };

  /**
   * Handles event deletion
   *
   * @reason Removes event from organization
   */
  const handleDelete = async (): Promise<void> => {
    if (!event) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the event "${event.name}"? This action cannot be undone.`,
    );

    if (!confirmDelete) return;

    setError(null);

    try {
      await deleteEventMutation.mutateAsync(event.id);

      // Invalidate event list to refresh
      await queryClient.invalidateQueries(LIST_EVENTS_CACHE_KEY);

      // Close dialog and reset form
      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete event';
      setError(errorMessage);
    }
  };

  /**
   * Handles dialog close and resets form
   *
   * @reason Cleans up form state when dialog is closed
   */
  const handleClose = (): void => {
    setName('');
    setDescription('');
    setIsActive(true);
    setError(null);
    onClose();
  };

  const isLoading = updateEventMutation.isLoading || deleteEventMutation.isLoading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Event</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="dense"
          label="Event Name"
          type="text"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isLoading}
          sx={{ mb: 2, mt: 1 }}
        />

        <TextField
          margin="dense"
          label="Description"
          type="text"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isLoading}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={isLoading}
            />
          }
          label="Active"
        />

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDelete} color="error" disabled={isLoading}>
          Delete
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleUpdate} variant="contained" disabled={isLoading}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEventDialog;
